import { Minus, Plus, ShoppingBag, Smartphone, Trash2, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart, cartTotal, cartDiscount, cartMessage, isAccessory } from "@/lib/cart";
import { formatPrice, waLink, conditionLabel, accDetails, categoryOf } from "@/lib/store";
import { useDelivery, cartShipping, scopeOf } from "@/lib/delivery";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { FreightQuote } from "./FreightQuote";

const QtyControl = ({ it }) => {
  const { setQty } = useCart();
  const btn = "flex h-7 w-7 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10";
  return (
    <div className="flex items-center rounded-full border border-white/15">
      <button data-testid={`cart-qty-minus-${it.key}`} aria-label="Diminuir" onClick={() => setQty(it.key, it.qty - 1)} className={btn}><Minus className="h-3 w-3" /></button>
      <span data-testid={`cart-qty-${it.key}`} className="w-6 text-center text-sm tabular-nums">{it.qty}</span>
      <button data-testid={`cart-qty-plus-${it.key}`} aria-label="Aumentar" onClick={() => setQty(it.key, it.qty + 1)} className={btn}><Plus className="h-3 w-3" /></button>
    </div>
  );
};

const CartItem = ({ it }) => {
  const { remove } = useCart();
  const p = it.product;
  const details = (isAccessory(p) ? accDetails(p) : [p.storage, p.color, conditionLabel(p)]).filter(Boolean).join(" · ");
  return (
    <li data-testid={`cart-item-${it.key}`} className="flex gap-4 py-5">
      <div className="h-20 w-16 shrink-0 overflow-hidden rounded-xl bg-zinc-900">
        {p.images?.[0] ? <img src={p.images[0]} alt={p.model} loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <Smartphone className="m-auto mt-7 h-6 w-6 text-zinc-500" />}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <p className="font-semibold leading-tight text-white">{p.model}</p>
          <button data-testid={`cart-remove-${it.key}`} aria-label="Remover" onClick={() => remove(it.key)} className="text-zinc-400 transition-colors hover:text-white"><Trash2 className="h-4 w-4" /></button>
        </div>
        {it.forModel && <p className="mt-1 text-xs text-zinc-300">Para {it.forModel}</p>}
        {details && <p className="mt-1 text-xs text-zinc-400">{details}</p>}
        <div className="mt-auto flex items-center justify-between pt-3">
          {isAccessory(p) ? <QtyControl it={it} /> : <span className="text-xs text-zinc-400">1 unidade</span>}
          <p className="font-display font-bold text-white">{formatPrice(p.price * it.qty)}</p>
        </div>
      </div>
    </li>
  );
};

const EmptyCart = ({ onClose }) => (
  <div data-testid="cart-empty" className="flex flex-1 flex-col items-center justify-center text-center">
    <ShoppingBag className="h-10 w-10 text-zinc-500" strokeWidth={1.4} />
    <p className="mt-4 font-display text-2xl font-bold text-white">Seu carrinho está vazio</p>
    <p className="mt-2 text-sm text-zinc-400">Escolha um aparelho ou acessório para começar.</p>
    <button data-testid="cart-empty-browse" onClick={onClose} className="mt-6 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black">Ver produtos</button>
  </div>
);

export const CartDrawer = () => {
  const { items, open, setOpen, clear } = useCart();
  const total = cartTotal(items);
  const discount = cartDiscount(items);
  const d = useDelivery(); // a entrega consultada vai na mensagem do WhatsApp
  const freightItems = items.map((it) => ({ category: categoryOf(it.product), price: Number(it.product.price) || 0, quantity: Math.min(it.qty, 10) })).slice(0, 20);
  const shipping = cartShipping(d, scopeOf(freightItems));
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" side="right" className="flex w-full flex-col gap-0 border-white/10 bg-[#0d0d0f] p-0 text-white sm:max-w-md">
        <div className="border-b border-white/10 px-6 pb-5 pt-6">
          <SheetTitle className="font-display text-2xl font-bold text-white">Carrinho</SheetTitle>
          <SheetDescription className="mt-1 text-sm text-zinc-400">Finalize pelo WhatsApp e combine pagamento e entrega.</SheetDescription>
        </div>
        {items.length === 0 ? <EmptyCart onClose={() => setOpen(false)} /> : (
          <>
            <ul data-lenis-prevent className="flex-1 divide-y divide-white/10 overflow-y-auto px-6">
              {items.map((it) => <CartItem key={it.key} it={it} />)}
              <li className="py-5"><FreightQuote items={freightItems} testid="cart-freight" stacked /></li>
            </ul>
            <div className="border-t border-white/10 px-6 pb-6 pt-5">
              {(discount.total > 0 || shipping) && (
                <div data-testid="cart-summary" className="mb-3 space-y-1 text-sm">
                  <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>{formatPrice(total)}</span></div>
                  {discount.total > 0 && <div data-testid="cart-discount" className="space-y-1">{discount.lines.map((l) => (
                    <div key={l.label} className="flex justify-between text-emerald-300"><span>{l.label}</span><span>-{formatPrice(l.amount)}</span></div>
                  ))}</div>}
                  {shipping && (
                    <div data-testid="cart-shipping" className={`flex justify-between ${shipping.amount ? "text-zinc-200" : "text-emerald-300"}`}>
                      <span>{shipping.label}</span><span data-testid="cart-shipping-amount">{shipping.amount ? `+${formatPrice(shipping.amount)}` : "R$ 0,00"}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-end justify-between">
                <p className="mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">{shipping?.amount ? "Total com frete" : "Total"}</p>
                <p data-testid="cart-total" className="font-display text-3xl font-extrabold text-white">{formatPrice(total - discount.total + (shipping?.amount || 0))}</p>
              </div>
              <p className="mt-1 text-right text-xs text-zinc-400">{shipping ? "Parcelamos em até 21x." : "Calcule o frete acima para ver o total com entrega. Parcelamos em até 21x."}</p>
              <a data-testid="cart-checkout-whatsapp" href={waLink(cartMessage(items, shipping))} target="_blank" rel="noopener noreferrer"
                className="group mt-5 flex w-full items-center justify-between gap-4 rounded-full bg-[#25D366] py-2.5 pl-6 pr-2.5 text-black transition-transform duration-300 hover:scale-[1.02] active:scale-95">
                <span className="flex items-center gap-2.5 font-bold"><WhatsAppIcon className="h-5 w-5" /> Finalizar pelo WhatsApp</span>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black text-white transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
              </a>
              <button data-testid="cart-clear" onClick={clear} className="mt-3 w-full text-center text-xs text-zinc-400 underline-offset-4 hover:text-white hover:underline">Esvaziar carrinho</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
